import { sortBy } from 'lodash';
import { Rule } from 'antd/lib/form';
// Types
import {
  EditCampaignContentInput,
  EditCampaignTaskContentInput,
} from 'api/graphql-global-types';
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_activities_CustomActivity,
  GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity,
  GetCampaignsV2_getCampaigns_entities_activities_InPersonAppearanceActivity,
  GetCampaignsV2_getCampaigns_entities_activities_SocialActivity,
  GetCampaignsV2_getCampaigns_entities_activities_SocialBoostActivity,
  GetCampaignsV2_getCampaigns_entities_activities_SocialShareActivity,
  GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity,
  GetCampaignsV2_getCampaigns_entities_activities_UgcActivity,
  GetCampaignsV2_getCampaigns_entities_activities_VirtualAppearanceActivity,
  GetCampaignsV2_getCampaigns_entities_search_SocialBoostSearchCriteria_locations,
} from 'api/campaignV2/types/GetCampaignsV2';

export const getSocialActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_SocialActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'SocialActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_SocialActivity;

  return activity;
};

/**
 * UGC Activity has the same shape as SocialActivity but only contains content tasks (no post tasks).
 * We cast it to SocialActivity type since the fields are compatible.
 */
export const getUgcActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_UgcActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'UgcActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_UgcActivity;

  return activity;
};

export const getSocialShareActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_SocialShareActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'SocialShareActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_SocialShareActivity;

  return activity;
};

export const getSocialBoostActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_SocialBoostActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'SocialBoostActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_SocialBoostActivity;

  return activity;
};

export const getSponsoredAppearanceActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'SponsoredAppearanceActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity;

  return activity;
};

export const getVirtualAppearanceActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_VirtualAppearanceActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'VirtualAppearanceActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_VirtualAppearanceActivity;

  return activity;
};

export const getInPersonAppearanceActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_InPersonAppearanceActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'InPersonAppearanceActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_InPersonAppearanceActivity;

  return activity;
};

export const getCustomActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_CustomActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'CustomActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_CustomActivity;

  return activity;
};

/**
 * Custom campaigns (CampaignType.Custom) use the CustomBriefActivity shape.
 * This is distinct from getCustomActivity, which targets BookMe CustomActivity.
 */
export const getCustomBriefActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity => {
  const activity = record?.activities?.find(
    (item) => item.__typename === 'CustomBriefActivity'
  ) as GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity;

  return activity;
};

export const getSABio = (
  activity: GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity
): string | null => {
  return activity?.target?.__typename === 'SponsoredAppearanceGuest'
    ? activity?.target?.guestBio
    : activity?.target?.__typename === 'SponsoredAppearanceProduct'
    ? activity?.target?.productBio || ''
    : '';
};

export const getSAName = (
  activity: GetCampaignsV2_getCampaigns_entities_activities_SponsoredAppearanceActivity
): string | null => {
  return activity?.target?.__typename === 'SponsoredAppearanceGuest'
    ? activity?.target?.guestName
    : activity?.target?.__typename === 'SponsoredAppearanceProduct'
    ? activity?.target?.brandName
    : '';
};

// present readable location data in the form of "County code: US, Country name: United states"..
export function formatLocationString(
  location: GetCampaignsV2_getCampaigns_entities_search_SocialBoostSearchCriteria_locations
): string {
  const excludedKeys = ['__typename', 'placeId', 'latitude', 'longitude'];
  const keyRenames: Record<string, string> = {
    country: 'country code',
    placeText: 'country name',
  };

  return Object.entries(location)
    .filter(
      ([key, value]) =>
        !excludedKeys.includes(key) && value !== null && value !== ''
    )
    .map(([key, value]) => `${keyRenames[key] || key}: ${value}`)
    .join(', ');
}

export type ContentEditableActivity =
  | GetCampaignsV2_getCampaigns_entities_activities_CustomBriefActivity
  | GetCampaignsV2_getCampaigns_entities_activities_SocialActivity
  | GetCampaignsV2_getCampaigns_entities_activities_UgcActivity;

type ContentTaskSource = {
  id: string;
  orderIdx: number | null;
  title: string | null;
  description: string | null;
  suggestedCaption?: string | null;
  hashtags?: string[] | null;
  mentions?: string[] | null;
  tagAsCollab?: boolean;
  contentItems?: Omit<ContentTaskSource, 'contentItems'>[] | null;
};

export type CampaignContentItemFormValues = {
  id: string;
  title?: string | null;
  description?: string | null;
};

export type CampaignContentTaskFormValues = CampaignContentItemFormValues & {
  suggestedCaption?: string | null;
  hashtags?: string[];
  mentions?: string[];
  tagAsCollab?: boolean;
  contentItems?: CampaignContentItemFormValues[];
};

export type CampaignContentFormValues = {
  brandName?: string | null;
  website?: string | null;
  instructions?: string | null;
  encouraged?: string | null;
  avoid?: string | null;
  promoCodes?: string[];
  promoUrls?: string[];
  tasks?: CampaignContentTaskFormValues[];
};

export const getContentEditableActivity = (
  record: GetCampaignsV2_getCampaigns_entities
): ContentEditableActivity | null =>
  getCustomBriefActivity(record) ||
  getSocialActivity(record) ||
  getUgcActivity(record) ||
  null;

const getContentTasks = (
  activity: ContentEditableActivity
): ContentTaskSource[] => {
  switch (activity.__typename) {
    case 'CustomBriefActivity':
      return activity.tasksV2 ?? [];
    case 'SocialActivity':
      return activity.tasksV2?.length ? activity.tasksV2 : activity.tasks ?? [];
    default:
      return activity.tasks ?? [];
  }
};

const toNullableText = (value?: string | null): string | null =>
  value?.trim() || null;

const getTitleInput = (value?: string | null): { title?: string } => {
  const title = toNullableText(value);
  return title ? { title } : {};
};

export const getCampaignContentFormValues = (
  activity: ContentEditableActivity
): CampaignContentFormValues => ({
  brandName: activity.brandName,
  website: activity.website,
  instructions: activity.instructions,
  encouraged: activity.encouraged,
  avoid: activity.avoid,
  promoCodes: activity.promoCodes ?? [],
  promoUrls: activity.promoUrls ?? [],
  tasks: sortBy(getContentTasks(activity), 'orderIdx').map((task) => ({
    id: task.id,
    title: task.title,
    description: task.description,
    ...(task.suggestedCaption !== undefined && {
      suggestedCaption: task.suggestedCaption,
    }),
    ...(task.hashtags !== undefined && { hashtags: task.hashtags ?? [] }),
    ...(task.mentions !== undefined && { mentions: task.mentions ?? [] }),
    ...(task.tagAsCollab !== undefined && { tagAsCollab: task.tagAsCollab }),
    contentItems: sortBy(task.contentItems ?? [], 'orderIdx').map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description,
    })),
  })),
});

export const getEditCampaignContentInput = (
  campaignId: string,
  values: CampaignContentFormValues
): EditCampaignContentInput => ({
  campaignId,
  brandName: toNullableText(values.brandName),
  website: toNullableText(values.website),
  instructions: toNullableText(values.instructions),
  encouraged: toNullableText(values.encouraged),
  avoid: toNullableText(values.avoid),
  promoCodes: values.promoCodes ?? [],
  promoUrls: values.promoUrls ?? [],
  tasks: (values.tasks ?? []).flatMap(
    ({ contentItems, ...task }): EditCampaignTaskContentInput[] => [
      {
        id: task.id,
        ...getTitleInput(task.title),
        description: toNullableText(task.description),
        ...(task.suggestedCaption !== undefined && {
          suggestedCaption: toNullableText(task.suggestedCaption),
        }),
        ...(task.hashtags !== undefined && { hashtags: task.hashtags }),
        ...(task.mentions !== undefined && { mentions: task.mentions }),
        ...(task.tagAsCollab !== undefined && {
          tagAsCollab: task.tagAsCollab,
        }),
      },
      ...(contentItems ?? []).map((item) => ({
        id: item.id,
        ...getTitleInput(item.title),
        description: toNullableText(item.description),
      })),
    ]
  ),
});

export const getRequiredIfSetRules = (initialValue?: string | null): Rule[] => [
  {
    required: !!initialValue,
    whitespace: true,
    message: 'This field is required',
  },
];
